/*
	Warning component shown when a user is about to remove their own permissions
	to access a matter or matters, which would lock them out.

	This component calculates whether the warning should be shown based on the
	permission settings and the current user's membership in the allow lists.
*/

import React from "react";
import PropTypes from "prop-types";

export function SelfLockoutWarning({
	matterPermissionGlobalSetting,
	userAllowList,
	groupAllowList,
	currentUserSub,
	permissionsGroups,
	singular = true,
	isUptimeAdmin,
	mode = "edit", // "edit" or "create"
}) {
	// Only show warning if not set to "allUsers"
	if (matterPermissionGlobalSetting === "allUsers") {
		return null;
	}

	if (isUptimeAdmin) {
		// If user is an admin, do not show the warning since they will not be locked out
		return null;
	}

	// Check if user is in the user allow list
	const userIsInUserAllowList = userAllowList.some(
		(u) => u.userSub === currentUserSub
	);

	// Get all group IDs the current user is a member of
	const groupIDsUserIsIn = permissionsGroups
		.filter((pg) => pg.memberSubs && pg.memberSubs.includes(currentUserSub))
		.map((pg) => pg.id);

	// Check if user is in any of the allowed groups
	const userIsInGroupAllowList = groupAllowList.some((g) =>
		groupIDsUserIsIn.includes(g.groupID)
	);

	// If user is in either the user allow list or a group allow list, no warning
	if (userIsInUserAllowList || userIsInGroupAllowList) {
		return null;
	}

	// Show the warning
	const matterText = singular ? "this matter" : "these matters";

	// Different message based on mode
	const warningMessage =
		mode === "create"
			? `You are not granting yourself permissions to access ${matterText}. If you proceed you will not have access to ${matterText} and the documents/content within it. If you need access to ${matterText} later you will need to contact your administrator.`
			: `You are removing your own permissions to access ${matterText}. If you proceed you will no longer have access to ${matterText} and the documents/content within it. If you need access to ${matterText} later you will need to contact your administrator.`;

	return (
		<div
			style={{
				padding: 10,
				backgroundColor: "#fff3cd",
				border: "1px solid #ffeaa7",
				borderRadius: 4,
				color: "#856404",
				marginTop: 10,
			}}
		>
			{warningMessage}
		</div>
	);
}

SelfLockoutWarning.propTypes = {
	// Permission settings
	matterPermissionGlobalSetting: PropTypes.string.isRequired,
	userAllowList: PropTypes.array.isRequired,
	groupAllowList: PropTypes.array.isRequired,

	// Current user information
	currentUserSub: PropTypes.string.isRequired,
	permissionsGroups: PropTypes.array.isRequired,

	// Display options
	singular: PropTypes.bool, // true for "this matter", false for "these matters"
	isUptimeAdmin: PropTypes.bool.isRequired, // true if the user is an admin
	mode: PropTypes.oneOf(["edit", "create"]), // "edit" for editing existing matter, "create" for new matter
};

export default SelfLockoutWarning;
